import { useStrikeConfigValidation } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import type { DomainError } from '@core/domain';
import { Dialog, LoadingSpinner, TextInput, Text } from '@soteria/design-system-ve';
import { useState, useEffect, useCallback } from 'react';

import * as styles from './RollingWindowDialog.css';

const DRAFT_KEY_PREFIX = 'strike-rolling-window-draft-';

interface RollingWindowDialogProps {
  isOpen: boolean;
  currentDays: number;
  communityId: string;
  errors?: DomainError[] | null;
  onClose: () => void;
  onSave: (days: number) => Promise<boolean>;
}

/**
 * Dialog for editing the strike rolling window configuration.
 * Follows the ApiSettingsDialog pattern with validation and session storage draft preservation.
 */
export function RollingWindowDialog({ isOpen, currentDays, communityId, errors, onClose, onSave }: RollingWindowDialogProps) {
  const { validateRollingWindow } = useStrikeConfigValidation();

  const draftKey = `${DRAFT_KEY_PREFIX}${communityId}`;

  const [days, setDays] = useState<number>(currentDays);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Restore draft or reset to current value when dialog opens
  useEffect(() => {
    if (isOpen) {
      const draft = sessionStorage.getItem(draftKey);

      if (draft !== null) {
        const parsed = parseInt(draft, 10);

        if (!isNaN(parsed)) {
          setDays(parsed);
          setValidationError(validateRollingWindow(parsed));

          return;
        }
      }

      setDays(currentDays);
      setValidationError(null);
    }
  }, [isOpen, currentDays, draftKey, validateRollingWindow]);

  const handleDaysChange = useCallback(
    (value: string) => {
      const numValue = parseInt(value, 10);

      if (isNaN(numValue)) {
        setDays(0);
        setValidationError('Rolling window must be a whole number');
        sessionStorage.setItem(draftKey, '0');

        return;
      }

      setDays(numValue);
      setValidationError(validateRollingWindow(numValue));
      sessionStorage.setItem(draftKey, String(numValue));
    },
    [draftKey, validateRollingWindow]
  );

  const handleSubmit = useCallback(async () => {
    const error = validateRollingWindow(days);

    if (error) {
      setValidationError(error);

      return;
    }

    setIsSubmitting(true);

    try {
      const success = await onSave(days);

      if (success) {
        sessionStorage.removeItem(draftKey);
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [days, draftKey, onClose, onSave, validateRollingWindow]);

  const isFormValid = validationError === null && days > 0;

  return (
    <Dialog
      description="Set the number of days over which strikes accumulate before resetting."
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : 'Save',
        onClick: handleSubmit,
        disabled: isSubmitting || !isFormValid,
      }}
      size="md"
      title="Edit Rolling Window"
      onClose={onClose}
    >
      <div className={styles.content}>
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="Error saving rolling window:" />}

        <TextInput
          aria-describedby={validationError ? 'rolling-window-error' : undefined}
          aria-invalid={!!validationError}
          disabled={isSubmitting}
          label="Rolling Window (days)"
          max="365"
          min="1"
          type="number"
          value={days.toString()}
          onChange={handleDaysChange}
        />

        {validationError && (
          <Text className={styles.validationError} id="rolling-window-error" role="alert">
            {validationError}
          </Text>
        )}

        <Text className={styles.helperText}>Enter a value between 1 and 365 days. Default is 30 days.</Text>
      </div>
    </Dialog>
  );
}
